import { formatMoney } from '@shared/utils'
import type { PortfolioComposition, PortfolioOverview } from '../types/portfolioTypes'

const TRAILING_ZERO_DECIMAL = '.0'
const MAX_API_PERCENT_DECIMALS = 3

/**
 * A percentage exactly as the API sends it, unsigned: 2.07 → "2.07%",
 * 6.5 → "6.5%", 2 → "2%". Only a value with MORE than 3 decimals is rounded
 * (to 3): 2.07519 → "2.075%" — returns are business figures, so they are
 * never re-rounded below the precision the backend chose.
 */
export const formatApiPercent = (value: number): string =>
  `${parseFloat(Math.abs(value).toFixed(MAX_API_PERCENT_DECIMALS))}%`

/** One decimal, no trailing `.0`, unsigned — `38.3%`, `40%`. Direction is drawn by an arrow. */
export const formatCompactPercent = (value: number): string => {
  const oneDp = Math.abs(value).toFixed(1)
  const trimmed = oneDp.endsWith(TRAILING_ZERO_DECIMAL)
    ? oneDp.slice(0, -TRAILING_ZERO_DECIMAL.length)
    : oneDp
  return `${trimmed}%`
}

/**
 * The overview endpoint reports valuation and rent separately and never adds
 * them, so the headline is assembled here. Every part comes from that one
 * payload — mixing in the holdings summary's total (scoped differently) could
 * leave the three segments not summing to the figure printed above them.
 */
export function buildPortfolioComposition(data: PortfolioOverview): PortfolioComposition {
  const currency = data.totalInvested.currency
  const invested = data.totalInvested.value
  const current = data.currentPortfolioValue.value
  const rent = data.totalRentalIncome.value

  const appreciation = current - invested
  const totalReturn = appreciation + rent
  const returnPct = invested > 0 ? (totalReturn / invested) * 100 : 0

  return {
    totalValue: formatMoney(currency, current + rent),
    totalReturnPercent: `${totalReturn >= 0 ? '+' : '-'}${formatCompactPercent(returnPct)}`,
    isPositive: totalReturn >= 0,
    invested: formatMoney(currency, invested),
    appreciation: formatMoney(currency, Math.abs(appreciation)),
    isAppreciationPositive: appreciation >= 0,
    rentalIncome: formatMoney(currency, rent),
    investedWeight: Math.max(invested, 0),
    appreciationWeight: Math.max(appreciation, 0),
    rentalWeight: Math.max(rent, 0),
  }
}
